import { useEffect, useState } from 'react';
import { CalendarSync, ExternalLink, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getInvestOttawaEventsSyncStatus,
  syncInvestOttawaEvents,
} from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

type SyncStatus = {
  ranAt: string;
  locationSlug: string;
  created: number;
  updated: number;
  skipped: number;
  flagshipCount: number;
  eventCount: number;
  errors: string[];
};

const InvestOttawaEventsTab = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState<SyncStatus | null>(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const result = await getInvestOttawaEventsSyncStatus();
      setStatus(result);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await syncInvestOttawaEvents();
      setStatus(result);
      toast.success(
        `Sync complete: ${result.created} created, ${result.updated} updated`
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to sync Invest Ottawa events');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <CalendarSync className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Invest Ottawa Events Sync
            </h2>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 max-w-2xl">
            Pulls upcoming events from{' '}
            <a
              href="https://www.investottawa.ca/events/"
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              investottawa.ca/events
              <ExternalLink className="h-3 w-3" />
            </a>{' '}
            and flagship programs from{' '}
            <a
              href="https://www.investottawa.ca/flagship-events/"
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              investottawa.ca/flagship-events
              <ExternalLink className="h-3 w-3" />
            </a>
            . Runs automatically once per day at 6:00 AM.
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
      </div>

      <Alert>
        <AlertTitle>Import target</AlertTitle>
        <AlertDescription>
          Events are imported into the Bayview Yards location by default (
          <code className="text-xs">bayviewyards</code>). Override with the{' '}
          <code className="text-xs">INVEST_OTTAWA_EVENTS_LOCATION_SLUG</code> server env var.
        </AlertDescription>
      </Alert>

      {loading ? (
        <p className="text-sm text-gray-500">Loading sync status…</p>
      ) : status ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Last run" value={new Date(status.ranAt).toLocaleString()} />
            <Stat label="Location" value={status.locationSlug} />
            <Stat label="Created / Updated" value={`${status.created} / ${status.updated}`} />
            <Stat
              label="Flagship / Events"
              value={`${status.flagshipCount} / ${status.eventCount}`}
            />
          </div>
          {status.skipped > 0 && (
            <p className="text-sm text-amber-600">Skipped: {status.skipped}</p>
          )}
          {status.errors.length > 0 && (
            <div className="text-sm text-red-600 space-y-1">
              <p className="font-medium">Errors ({status.errors.length}):</p>
              <ul className="list-disc pl-5 max-h-40 overflow-y-auto">
                {status.errors.slice(0, 20).map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No sync has run yet.</p>
      )}
    </div>
  );
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

export default InvestOttawaEventsTab;
